/**
 * Capacitor native bridge initialization
 * Handles platform detection and native plugin setup
 */
import { Capacitor } from '@capacitor/core';

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'web' | 'ios' | 'android'

/**
 * Initialize native plugins when running inside Capacitor
 */
export async function initCapacitor() {
  if (!isNative) return;

  try {
    // Hide splash screen after app is ready
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch (e) {
    console.warn('SplashScreen plugin not available:', e);
  }

  try {
    // Configure status bar for dark theme
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    if (platform === 'android') {
      await StatusBar.setBackgroundColor({ color: '#09090B' });
    }
  } catch (e) {
    console.warn('StatusBar plugin not available:', e);
  }

  try {
    // Listen for app state changes (background/foreground)
    const { App } = await import('@capacitor/app');
    App.addListener('backButton', ({ canGoBack }) => {
      if (!canGoBack) {
        App.exitApp();
      } else {
        window.history.back();
      }
    });
  } catch (e) {
    console.warn('App plugin not available:', e);
  }
}

/**
 * Use native camera if available, fallback to web
 */
export async function takePhoto() {
  if (!isNative) return null;
  
  try {
    const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
    const image = await Camera.getPhoto({
      quality: 80,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Camera,
      width: 1024,
      height: 1024,
    });
    return image.dataUrl;
  } catch (e) {
    console.warn('Camera not available:', e);
    return null;
  }
}

/**
 * Trigger haptic feedback on native platforms
 */
export async function hapticFeedback(type = 'light') {
  if (!isNative) return;
  
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const styles = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    };
    await Haptics.impact({ style: styles[type] || ImpactStyle.Light });
  } catch (e) {
    // Silently fail - haptics are optional
  }
}
